import axios from 'axios';
import { normalizeArrayResponse } from '../utils/normalizeApiResponse';

// Use server-side proxy for Azure Functions to avoid public CORS proxies
const AZURE_API_BASE = '/api/azure';

const azureClient = axios.create({
  baseURL: AZURE_API_BASE,
  headers: {
    'x-requested-with': 'XMLHttpRequest',
  },
});

// Token cache to avoid repeated token requests
let cachedToken = null;
let tokenExpiry = null;
const TOKEN_REFRESH_BUFFER = 10 * 60 * 1000; // Refresh 10 minutes before expiry
// Backoff when auth endpoints are failing to avoid tight retry loops in the browser
let lastAuthFailure = 0;
const AUTH_FAILURE_BACKOFF = 60 * 1000; // 60s

// Acquire access token from server-side proxy endpoints
const getAccessToken = async () => {
  // If running in local development, skip acquiring a Microsoft access token.
  // This prevents the browser from attempting auth flows while developing locally.
  // Note: We check for localhost to ensure this also works when running the production build locally.
  if (import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    throw new Error('Local mode: skip acquiring access token');
  }

  if (cachedToken && tokenExpiry && Date.now() < tokenExpiry - TOKEN_REFRESH_BUFFER) {
    return cachedToken;
  }

  if (tokenExpiry && Date.now() >= tokenExpiry) {
    cachedToken = null;
    tokenExpiry = null;
  }

  if (Date.now() - lastAuthFailure < AUTH_FAILURE_BACKOFF) {
    throw new Error('Auth endpoint failing - backing off');
  }

  try {
    const resp = await azureClient.get('/getAuth');
    if (resp?.data?.access_token) {
      cachedToken = resp.data.access_token;
      tokenExpiry = Date.now() + (resp.data.expires_in || 50 * 60) * 1000;
      return cachedToken;
    }
  } catch (err) {
    if (err?.response?.status === 403) lastAuthFailure = Date.now();
  }

  try {
    const resp = await azureClient.post('/dynamicGetAuth', { company: 'Condo Shield' });
    if (resp?.data?.access_token) {
      cachedToken = resp.data.access_token;
      tokenExpiry = Date.now() + (resp.data.expires_in || 50 * 60) * 1000;
      return cachedToken;
    }
  } catch (err) {
    if (err?.response?.status === 403) lastAuthFailure = Date.now();
  }

  lastAuthFailure = Date.now();
  throw new Error('Unable to acquire access token');
};

// Request interceptor: add Authorization header and pagination defaults
azureClient.interceptors.request.use(async (config) => {
  // Skip adding token for auth endpoints
  if (config.url && (config.url.includes('/getAuth') || config.url.includes('/dynamicGetAuth'))) {
    return config;
  }

  try {
    const token = await getAccessToken();
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  } catch (err) {
    // If token acquisition failed, proceed without token and let server respond
  }

  if (config.method && config.method.toLowerCase() === 'get') {
    config.params = config.params || {};
    // Only add defaults if not already present in params OR in the URL string
    const hasPage = config.params.page !== undefined || (config.url && config.url.includes('page='));
    const hasPageSize = config.params.pageSize !== undefined || (config.url && config.url.includes('pageSize='));
    
    if (!hasPage) config.params.page = 1;
    if (!hasPageSize) config.params.pageSize = 50;
  }

  return config;
});

// Response interceptor: retry once on 401 after clearing cache
azureClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config || {};
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      cachedToken = null;
      tokenExpiry = null;
      try {
        return azureClient(originalRequest);
      } catch (e) {
        throw e;
      }
    }
    return Promise.reject(error);
  }
);

// Cache for in-progress paginated requests to prevent loops/parallel redundant fetches
const activePaginatedRequests = new Map();
// Cache for completed paginated results (TTL: 2 minutes)
const paginatedResultsCache = new Map();
const PAGINATED_CACHE_TTL = 2 * 60 * 1000;

// Periodic cache cleanup to prevent memory leaks
// Runs every 60 seconds to remove expired entries
setInterval(() => {
  const now = Date.now();
  let cleaned = 0;
  for (const [key, value] of paginatedResultsCache) {
    if (now - value.timestamp > PAGINATED_CACHE_TTL) {
      paginatedResultsCache.delete(key);
      cleaned++;
    }
  }
  if (cleaned > 0) {
    console.log(`[azureClient] Cache cleanup: removed ${cleaned} expired entries`);
  }
}, 60000);

// Convenience: paginated associations helper
azureClient.getPaginatedAssociations = async (params = {}) => {
  const containerId = params.containerId || 'associations';
  const databaseId = params.databaseId || 'condoshield';
  const cacheKey = `${databaseId}:${containerId}:${JSON.stringify(params)}`;

  // Check results cache first
  if (paginatedResultsCache.has(cacheKey)) {
    const cached = paginatedResultsCache.get(cacheKey);
    if (Date.now() - cached.timestamp < PAGINATED_CACHE_TTL) {
      console.log(`[azureClient] Returning cached result for ${cacheKey}`);
      return cached.data;
    }
    paginatedResultsCache.delete(cacheKey);
  }

  // If specific page requested, just get that page
  if (params.page) {
    const resp = await azureClient.get('/getAll', {
      params: {
        databaseId,
        containerId,
        ...params,
      },
    });
    return normalizeArrayResponse(resp, 'getPaginatedAssociations');
  }

  // Check if a request for this exact set of params is already in progress
  if (activePaginatedRequests.has(cacheKey)) {
    console.log(`[azureClient] Returning existing in-progress request for ${cacheKey}`);
    return activePaginatedRequests.get(cacheKey);
  }

  const fetchAll = async () => {
    const recordMap = new Map();
    let page = 1;
    const pageSize = 100;
    
    try {
      while (true) {
        const resp = await azureClient.get('/getAll', {
          params: {
            databaseId,
            containerId,
            page,
            pageSize,
            ...params,
          },
        });
        
        const records = normalizeArrayResponse(resp, 'getPaginatedAssociations_loop');
        if (!records || records.length === 0) break;
        
        let newRecordsCount = 0;
        for (const rec of records) {
          if (rec && rec.id && !recordMap.has(rec.id)) {
            recordMap.set(rec.id, rec);
            newRecordsCount++;
          }
        }
        
        // If no new records were found on this page, we've reached the end or pagination is broken
        if (newRecordsCount === 0) break;
        
        // If we got fewer records than requested, we've reached the end
        if (records.length < pageSize) break;
        
        page++;
        // Safety break at 50 pages (5000 records) to prevent browser freeze
        if (page > 50) {
          console.warn(`getPaginatedAssociations: Scanned 50 pages for ${containerId}. Stopping to prevent freeze.`);
          break;
        }
      }
    } catch (e) {
      console.error(`Error fetching page ${page} for ${containerId}:`, e);
    } finally {
      activePaginatedRequests.delete(cacheKey);
    }
    
    const result = Array.from(recordMap.values());
    // Store in results cache
    paginatedResultsCache.set(cacheKey, {
      data: result,
      timestamp: Date.now()
    });
    return result;
  };

  const requestPromise = fetchAll();
  activePaginatedRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

// Helper to clear paginated cache (useful after updates)
azureClient.clearPaginatedCache = () => {
  paginatedResultsCache.clear();
  console.log('[azureClient] Paginated cache cleared');
};

// NEW: Filtered associations query - gets ONLY records where recordType='association'
azureClient.getFilteredAssociations = async (params = {}) => {
  const containerId = params.containerId || 'associations';
  const databaseId = params.databaseId || 'condoshield';
  const cacheKey = `filtered:${databaseId}:${containerId}`;

  // Check results cache first
  if (paginatedResultsCache.has(cacheKey)) {
    const cached = paginatedResultsCache.get(cacheKey);
    if (Date.now() - cached.timestamp < PAGINATED_CACHE_TTL) {
      console.log(`[azureClient] Returning cached filtered result for ${cacheKey}`);
      return cached.data;
    }
    paginatedResultsCache.delete(cacheKey);
  }

  const fetchFiltered = async () => {
    const recordMap = new Map();
    let page = 1;
    const pageSize = 100;
    let totalScanned = 0;
    
    try {
      while (true) {
        const resp = await azureClient.get('/getAll', {
          params: {
            databaseId,
            containerId,
            page,
            pageSize,
          },
        });
        
        const records = normalizeArrayResponse(resp, 'getFilteredAssociations');
        if (!records || records.length === 0) break;
        
        totalScanned += records.length;
        let newRecordsCount = 0;
        
        // Filter: ONLY include records where recordType === 'association' OR recordType is undefined (legacy)
        for (const rec of records) {
          if (rec && rec.id) {
            const isAssociation = rec.recordType === 'association' || !rec.recordType;
            if (isAssociation && !recordMap.has(rec.id)) {
              recordMap.set(rec.id, rec);
              newRecordsCount++;
            }
          }
        }
        
        // If no new records were found on this page, we've reached the end
        if (records.length < pageSize) break;
        
        page++;
        // Safety break at 10 pages since we're filtering
        if (page > 10) {
          console.log(`[getFilteredAssociations] Scanned ${page} pages (${totalScanned} total records). Stopping.`);
          break;
        }
      }
    } catch (e) {
      console.error(`Error fetching filtered associations page ${page}:`, e);
    }
    
    const result = Array.from(recordMap.values());
    console.log(`[getFilteredAssociations] Found ${result.length} associations from ${totalScanned} total records scanned`);
    
    // Store in results cache
    paginatedResultsCache.set(cacheKey, {
      data: result,
      timestamp: Date.now()
    });
    return result;
  };

  return fetchFiltered();
};

export default azureClient;
